/**
 * Grid Control — Main React Component
 *
 * Renders a config-driven data table inside an Atlas Form.
 * Supports: inline editing (bulkEditEnabled), per-row modal edit, row selection,
 * add / delete / quick-add actions, pagination, drag-reorder, CSV/JSON export/import.
 *
 * SRP: rendering and interaction only.
 * Data shapes → grid.types.ts | Pure logic → grid.helpers.ts | Modal → GridEditModal.tsx
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './grid.css';
import type { ControlRendererProps } from '../../ControlRenderer';
import type { GridConfig, GridColumn, GridRow, GridButton, GridValidationErrors, CardTemplateConfig } from './grid.types';
import { GridActionRegistry } from './grid.action-registry';
import { GridItemPresetsRegistry } from './grid.item-presets';
import {
  createNewRow,
  stampRowId,
  evaluateCellClass,
  evaluateCompute,
  getCellValue,
  setCellValue,
  validateCell,
  serializeRow,
  exportToJson,
  exportToCsv,
  importFromJson,
  importFromCsv,
  reorderRows,
  getPage,
  totalPages,
  resolveLinkAction,
  interpolateTemplate,
  getDeep,
} from './grid.helpers';
import { GridEditModal } from './GridEditModal';
// Real, already-established shared confirmation dialog (checked `ui-components-react` first,
// per this codebase's "find and reuse the real pattern" convention, rather than building a
// private one-off inside this package -- a first pass here mistakenly did exactly that before
// this was found; corrected to use the genuine shared component instead).
import { ConfirmDialog } from '@atlas-forms/ui-components-react';
import { useFormTheme } from '../../../contexts/FormThemeContext';
import { getAtlasApiConfig, getCsrfToken, getCsrfTokenHeaderName } from '@atlas-forms/client-js';
import { fetchAuthToken } from '@passport/auth-session';
import { isInRole } from '../../../utils/roleAccess';

// Revoking right after click() can race the browser's save and corrupt the download;
// keep the blob URL alive long enough for the download to start.
const BLOB_URL_REVOKE_DELAY_MS = 2000;

// ─── Renderers ────────────────────────────────────────────────────────────────

/** Named renderers for computed columns */
const STATUS_BADGE_RENDERER = 'statusBadge';

const renderComputedValue = (col: GridColumn, raw: unknown): React.ReactNode => {
  if (col.renderer === STATUS_BADGE_RENDERER) {
    const str = String(raw ?? '');
    return (
      <span className={`af-grid__badge af-grid__badge--${str.toLowerCase().replace(/\s+/g, '-')}`}>
        {str}
      </span>
    );
  }
  return String(raw ?? '');
};

// ─── Inline cell editor ───────────────────────────────────────────────────────

interface CellEditorProps {
  col: GridColumn;
  value: unknown;
  error?: string;
  onChange: (val: unknown) => void;
  onBlur: () => void;
}

const CellEditor: React.FC<CellEditorProps> = ({ col, value, error, onChange, onBlur }) => {
  const type = col.type ?? 'text';

  const base: React.InputHTMLAttributes<HTMLInputElement> = {
    className: `af-grid__cell-input${error ? ' af-grid__cell-input--error' : ''}`,
    onBlur,
  };

  switch (type) {
    case 'select':
      return (
        <select
          className={`af-grid__cell-select${error ? ' af-grid__cell-select--error' : ''}`}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        >
          {(col.options ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );

    case 'toggle':
      return (
        <label className="af-grid__cell-toggle-wrapper">
          <input
            type="checkbox"
            className="af-grid__cell-toggle"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            onBlur={onBlur}
          />
          {!Boolean(value) && (
            <span className="af-grid__cell-toggle-hint">
              {col.placeholder ?? col.label}
            </span>
          )}
        </label>
      );

    case 'number':
      return (
        <input
          {...base}
          type="number"
          value={String(value ?? '')}
          min={col.min}
          max={col.max}
          step={col.step}
          placeholder={col.placeholder ?? col.label}
          onChange={(e) => onChange(e.target.valueAsNumber)}
        />
      );

    case 'date':
      return (
        <input
          {...base}
          type="date"
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    default: // text | css-class
      return (
        <input
          {...base}
          type="text"
          value={String(value ?? '')}
          placeholder={col.placeholder ?? col.label}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
};

// ─── Link action button ─────────────────────────────────────────────────────────
//
// Extracted from the original single-piece `LinkCell` (Task 19) so the exact same row-action
// button + delete-confirm logic can be reused OUTSIDE a `<td>` — card/gallery view (below)
// renders row actions inline in a card footer, not a table cell, but must behave identically:
// same visibility/role gating, same DELETE round-trip (including the real request-body fix and
// in-app ConfirmDialog, not a re-implementation of either). `LinkCell` (still used by the
// unchanged table view) is now just this component wrapped in a `<td>`.

interface LinkActionButtonProps {
  col: GridColumn;
  row: GridRow;
  onOpenForm: (
    rowId: string,
    formId: number,
    mode: 'view' | 'edit',
    saveEndpoint?: string,
    saveMethod?: 'PUT' | 'POST',
    dataRootPath?: string,
  ) => void;
  /** Called after a `kind: 'delete'` link action's DELETE request succeeds — removes the row locally. */
  onRowDeleted: (rowId: string) => void;
}

const LinkActionButton: React.FC<LinkActionButtonProps> = React.memo(({ col, row, onOpenForm, onRowDeleted }) => {
  // useMemo so `linkCfg` stays reference-stable when col.link is undefined across renders
  const linkCfg = useMemo(() => col.link ?? {}, [col.link]);
  const [isDeleting, setIsDeleting] = useState(false);
  // Real bug found 2026-09-03: this action's `confirmMessage` was shown via native
  // `window.confirm()` — a jarring, unstyled browser alert, not an in-app dialog. Replaced
  // with `ConfirmDialog` (reuses this package's own existing modal chrome). `pendingDelete`
  // holds the message to show while awaiting the user's choice; `null` means no dialog open.
  const [pendingDelete, setPendingDelete] = useState<{ message: string; endpoint: string; method: string } | null>(null);

  let isVisible = true;
  if (linkCfg.visible) {
    try {
      // eslint-disable-next-line no-new-func
      isVisible = Boolean(new Function('row', `return !!(${linkCfg.visible})`)(row));
    } catch (err) {
      console.warn('[GridControl] Link visibility expression error:', err);
    }
  }

  // Role gating: rendered (never hidden) but disabled when the current user lacks any
  // of `requiredRoles`. Deliberately independent of `isVisible` above (which hides
  // per-row via a data expression — a different concern from permission gating).
  const roleAllowed = isInRole(linkCfg.requiredRoles);

  const performDelete = useCallback(async (endpoint: string, method: string) => {
    setIsDeleting(true);
    try {
      const { baseUrl } = getAtlasApiConfig();
      const headers: Record<string, string> = {};
      try {
        const session = fetchAuthToken();
        if (session?.token) headers.Authorization = `Bearer ${session.token}`;
      } catch (err) {
        console.warn('[GridControl] fetchAuthToken() failed; deleting without an Authorization header:', err);
      }
      try {
        const csrfToken = await getCsrfToken();
        if (csrfToken) headers[getCsrfTokenHeaderName()] = csrfToken;
      } catch (err) {
        console.warn('[GridControl] getCsrfToken() failed; deleting without a CSRF header:', err);
      }
      // Real bug found 2026-09-03: this request previously carried no body at all. This
      // codebase's Go controllers' generic delete endpoints (`DeleteWebRequest` — see
      // `BizFirstFi.Go.Essentials.Domain.Requests.DeleteWebRequest`, shared by every Go
      // entity's DELETE action, not just Documents) read the record ID from the BODY
      // (`{ id: { id: N } }`, the same `IDInfo` shape used throughout this API family),
      // not just the URL path — confirmed live against `/api/v1/documents/{id}`, which
      // 404'd ("Document with ID 0 not found") on a bodyless request and succeeded once
      // `{ id: { id: N } }` was sent. Extracted generically from the resolved endpoint's
      // trailing numeric path segment (however `endpointTemplate` interpolated the row's
      // ID into the URL), not hardcoded to any one entity's ID field name.
      headers['Content-Type'] = 'application/json';
      const idMatch = endpoint.match(/(\d+)\/?$/);
      const recordId = idMatch ? Number(idMatch[1]) : undefined;
      const res = await fetch(`${baseUrl}${endpoint}`, {
        method,
        headers,
        credentials: 'include',
        body: JSON.stringify(recordId !== undefined ? { id: { id: recordId } } : {}),
      });
      if (!res.ok) {
        throw new Error(`Delete failed: HTTP ${res.status}`);
      }
      onRowDeleted(row._rowId);
    } catch (err) {
      console.error('[GridControl] Delete link action failed:', err);
      window.alert(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  }, [row, onRowDeleted]);

  const handleClick = useCallback(async () => {
    if (!linkCfg.action) return;
    const resolved = resolveLinkAction(linkCfg.action, row);
    if (!resolved) return;

    if (resolved.kind === 'form') {
      onOpenForm(
        row._rowId,
        resolved.formId,
        resolved.mode ?? 'edit',
        resolved.saveEndpoint,
        resolved.saveMethod,
        resolved.dataRootPath,
      );
      return;
    }

    if (resolved.kind === 'delete') {
      // Real bug found 2026-09-03 ("unprofessional" per direct user feedback): this used
      // native `window.confirm()` — a jarring, unstyled browser alert. Now shows the SAME
      // schema-configured `confirmMessage` via ConfirmDialog (a real in-app modal, see that
      // component's own doc comment) instead of blocking synchronously; the actual delete
      // only proceeds from ConfirmDialog's onConfirm below.
      if (resolved.confirmMessage) {
        setPendingDelete({ message: resolved.confirmMessage, endpoint: resolved.endpoint, method: resolved.method });
        return;
      }
      await performDelete(resolved.endpoint, resolved.method);
      return;
    }

    const tgt = resolved.target ?? '_blank';
    window.open(resolved.url, tgt === '_self' ? '_self' : '_blank', 'noopener,noreferrer');
  }, [linkCfg, row, onOpenForm, performDelete]);

  const label = linkCfg.label ? interpolateTemplate(linkCfg.label, row) : 'Open';
  const showAs = linkCfg.showAs ?? 'button';
  const variant = linkCfg.variant ?? 'secondary';
  const isDisabled = !roleAllowed || isDeleting;

  return (
    <>
      {isVisible && (
        <button
          type="button"
          className={showAs === 'link' ? 'af-grid__link-text' : `af-grid__link-btn af-grid__link-btn--${variant}`}
          onClick={handleClick}
          disabled={isDisabled}
          title={!roleAllowed ? 'You do not have permission to perform this action' : undefined}
          aria-disabled={isDisabled}
        >
          {linkCfg.icon && (
            <span className={`af-grid__link-icon icon-${linkCfg.icon}`} aria-hidden="true" />
          )}
          {isDeleting ? 'Deleting…' : label}
        </button>
      )}
      <ConfirmDialog
        isOpen={pendingDelete !== null}
        title="Confirm Delete"
        message={pendingDelete?.message ?? ''}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmVariant="danger"
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          const target = pendingDelete;
          setPendingDelete(null);
          if (target) void performDelete(target.endpoint, target.method);
        }}
      />
    </>
  );
});
LinkActionButton.displayName = 'LinkActionButton';

// ─── Link cell (table view) ────────────────────────────────────────────────────

interface LinkCellProps {
  col: GridColumn;
  row: GridRow;
  onOpenForm: (rowId: string, formId: number, mode: 'view' | 'edit') => void;
  onRowDeleted: (rowId: string) => void;
  /** Sticky-positioning style when `col.freezePosition` is set — see GridControl's getFreezeStyle(). */
  freezeStyle?: React.CSSProperties;
}

const LinkCell: React.FC<LinkCellProps> = React.memo(({ col, row, onOpenForm, onRowDeleted, freezeStyle }) => (
  <td className="af-grid__td af-grid__td--link" style={freezeStyle}>
    <LinkActionButton col={col} row={row} onOpenForm={onOpenForm} onRowDeleted={onRowDeleted} />
  </td>
));
LinkCell.displayName = 'LinkCell';

// ─── Read-only cell ───────────────────────────────────────────────────────────

const renderReadOnlyCell = (col: GridColumn, value: unknown): React.ReactNode => {
  const type = col.type ?? 'text';

  if (type === 'computed') return renderComputedValue(col, value);
  if (type === 'toggle') return <input type="checkbox" checked={Boolean(value)} readOnly />;
  if (type === 'select') {
    const optionLabel = (col.options ?? []).find((o) => o.value === value)?.label ?? String(value ?? '');
    return <span>{optionLabel}</span>;
  }
  if (type === 'css-class') {
    return <span className={String(value ?? '')}>{String(value ?? '')}</span>;
  }
  return <span>{String(value ?? '')}</span>;
};

// ─── Card / gallery view ────────────────────────────────────────────────────────
//
// Alternate per-row rendering for `viewMode: 'cards' | 'gallery'` (Task 19). Reuses the exact
// same `displayRows` / selection / row-action plumbing the table view uses — this component
// only replaces the per-row MARKUP; data-fetch, pagination and row actions all live in
// GridControl above it, unchanged. `'cards'` and `'gallery'` deliberately share one
// implementation (varied only via the `variant` prop — gallery is image-forward with a larger
// image and a caption-style title/subtitle; cards balances title/description/meta) rather than
// two near-duplicate components, per this codebase's reuse-over-duplication convention.

/** Column label lookup for a card-template field key — falls back to the raw key when no
 *  matching column exists (a template field need not have a visible column). */
const labelForField = (columns: GridColumn[], key: string): string =>
  columns.find((c) => c.key === key)?.label || key;

interface GridCardViewProps {
  rows: GridRow[];
  /** Data columns (including `type: 'link'` action columns) — same list the table view uses. */
  columns: GridColumn[];
  cardTemplate: CardTemplateConfig | undefined;
  variant: 'cards' | 'gallery';
  emptyMessage: string;
  showCheckbox: boolean;
  isMultiSelect: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (rowId: string) => void;
  onOpenForm: (rowId: string, formId: number, mode: 'view' | 'edit') => void;
  onRowDeleted: (rowId: string) => void;
}

const GridCardView: React.FC<GridCardViewProps> = ({
  rows, columns, cardTemplate, variant, emptyMessage, showCheckbox, isMultiSelect,
  selectedIds, onToggleSelect, onOpenForm, onRowDeleted,
}) => {
  const linkColumns = useMemo(() => columns.filter((c) => c.type === 'link'), [columns]);

  // Fallback when a 'cards'/'gallery' grid is missing its template — degrade to the first
  // eligible data column as the title rather than rendering blank cards silently. Mirrors this
  // codebase's "warn, don't silently no-op on an unsupported/incomplete config" convention (see
  // FormWidgetHandler.ts's own searchResultViewMode handling).
  const effectiveTemplate = useMemo<CardTemplateConfig>(() => {
    if (cardTemplate?.titleField) return cardTemplate;
    const fallbackCol = columns.find(
      (c) => c.type !== 'link' && c.type !== 'selector' && c.type !== 'computed',
    );
    if (!cardTemplate?.titleField) {
      console.warn(
        '[GridControl] viewMode is "cards"/"gallery" but cardTemplate.titleField is not configured — falling back to the first data column as the title.',
      );
    }
    return { ...cardTemplate, titleField: cardTemplate?.titleField || fallbackCol?.key || '' };
  }, [cardTemplate, columns]);

  if (rows.length === 0) {
    return <div className="af-grid__cards-empty">{emptyMessage}</div>;
  }

  return (
    <div className={`af-grid__cards af-grid__cards--${variant}`}>
      {rows.map((row) => {
        const isSelected = selectedIds.has(row._rowId);
        const title = String(getDeep(row, effectiveTemplate.titleField) ?? '');
        const imageUrl = effectiveTemplate.imageField ? getDeep(row, effectiveTemplate.imageField) : undefined;
        const subtitle = effectiveTemplate.subtitleField ? getDeep(row, effectiveTemplate.subtitleField) : undefined;
        const description = effectiveTemplate.descriptionField ? getDeep(row, effectiveTemplate.descriptionField) : undefined;
        const metaFields = effectiveTemplate.metaFields ?? [];

        return (
          <div key={row._rowId} className={`af-grid__card${isSelected ? ' af-grid__card--selected' : ''}`}>
            {showCheckbox && (
              <label className="af-grid__card-select" onClick={(e) => e.stopPropagation()}>
                <input
                  type={isMultiSelect ? 'checkbox' : 'radio'}
                  checked={isSelected}
                  onChange={() => onToggleSelect(row._rowId)}
                  aria-label={`Select ${title || 'row'}`}
                />
              </label>
            )}

            {/* Gallery is inherently image-forward, so it always reserves an image slot (real
                image when imageUrl resolves, a placeholder pattern otherwise) — this is what
                makes it meaningfully different from Cards even for a grid whose data has no
                image URL field yet. Cards, by contrast, only shows an image slot when the
                template explicitly maps one (imageField set) — an image is optional content
                there, not the mode's defining trait. */}
            {(variant === 'gallery' || effectiveTemplate.imageField) && (
              imageUrl ? (
                <div className="af-grid__card-image">
                  <img src={String(imageUrl)} alt={title} loading="lazy" />
                </div>
              ) : (
                <div className="af-grid__card-image af-grid__card-image--placeholder" aria-hidden="true" />
              )
            )}

            <div className="af-grid__card-body">
              <div className="af-grid__card-title">{title || '—'}</div>
              {subtitle != null && subtitle !== '' && (
                <div className="af-grid__card-subtitle">{String(subtitle)}</div>
              )}
              {description != null && description !== '' && (
                <div className="af-grid__card-description">{String(description)}</div>
              )}
              {metaFields.length > 0 && (
                <div className="af-grid__card-meta">
                  {metaFields.map((key) => {
                    const val = getDeep(row, key);
                    if (val === null || val === undefined || val === '') return null;
                    return (
                      <span key={key} className="af-grid__card-meta-chip" title={labelForField(columns, key)}>
                        {String(val)}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            {linkColumns.length > 0 && (
              <div className="af-grid__card-actions">
                {linkColumns.map((col) => (
                  <LinkActionButton key={col.key} col={col} row={row} onOpenForm={onOpenForm} onRowDeleted={onRowDeleted} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
GridCardView.displayName = 'GridCardView';

// ─── Main component ───────────────────────────────────────────────────────────

export const GridControl: React.FC<ControlRendererProps> = ({ control, value, onChange, mode }) => {
  const theme = useFormTheme();
  const config = (control.config ?? {}) as GridConfig;
  // Use ?? instead of destructuring defaults — destructuring defaults only apply to undefined,
  // not to null, which can appear when configs are loaded from JSON.
  // Normalise columns: JSON configs may use "id" instead of "key" — unify to "key".
  const columns = (config.columns ?? []).map((col) =>
    col.key ? col : { ...col, key: col.id ?? '' },
  );
  const buttons = config.buttons ?? [];
  const features = config.features ?? {};
  const newEntityTemplate = config.newEntityTemplate ?? {};
  const emptyMessage = config.emptyMessage ?? 'No rows yet.';
  const idField = config.idField;
  const pageSize = config.pageSize;
  const editFormID = config.editFormID;
  const bulkEditEnabled = config.bulkEditEnabled ?? false;
  const tableClass = config.tableClass;
  const altRowClass = config.altRowClass;
  // Task 19 — result-rendering mode. Unset/'grid' is the existing table, byte-for-byte
  // unchanged; every grid built before this field existed keeps rendering exactly as before.
  const viewMode = config.viewMode ?? 'grid';
  const cardTemplate = config.cardTemplate;

  // ── Internal rows state ──────────────────────────────────────────────────

  const [rows, setRows] = useState<GridRow[]>(() => {
    const initial = Array.isArray(value) ? (value as Record<string, unknown>[]) : [];
    return initial.map(stampRowId);
  });

  // Sync external value → internal rows (only when value identity changes AND the change
  // did not originate from our own emitChange — otherwise we'd re-stamp with fresh _rowIds
  // and invalidate any active selection or open edit modal).
  const prevValueRef = useRef(value);
  useEffect(() => {
    if (value !== prevValueRef.current) {
      prevValueRef.current = value;
      if (value === lastEmittedRef.current) return; // our own echo — skip
      if (Array.isArray(value)) {
        setRows((value as Record<string, unknown>[]).map(stampRowId));
      }
    }
  }, [value]);

  // Always-fresh ref — lets callbacks read latest rows without stale closure issues
  const rowsRef = useRef<GridRow[]>(rows);
  rowsRef.current = rows;

  // Track the last value we emitted so the sync effect can skip re-stamping
  // our own data (which would assign new _rowIds and invalidate selections).
  const lastEmittedRef = useRef<unknown>(undefined);

  // Emit serialised rows whenever rows change
  const emitChange = useCallback(
    (nextRows: GridRow[]) => {
      if (onChange) {
        const serialized = nextRows.map((r) => serializeRow(r, columns, idField));
        lastEmittedRef.current = serialized;
        onChange(serialized);
      }
    },
    [onChange, columns, idField],
  );

  // ── Selection ────────────────────────────────────────────────────────────

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const selectorCol = columns.find((c) => c.type === 'selector');
  const isMultiSelect = !selectorCol || selectorCol.selectorType !== 'radio';

  const toggleSelect = useCallback(
    (rowId: string) => {
      setSelectedIds((prev) => {
        if (isMultiSelect) {
          const next = new Set(prev);
          if (next.has(rowId)) next.delete(rowId);
          else next.add(rowId);
          return next;
        }
        // radio: single selection
        return prev.has(rowId) ? new Set() : new Set([rowId]);
      });
    },
    [isMultiSelect],
  );

  const selectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedIds(new Set(rows.map((r) => r._rowId)));
      } else {
        setSelectedIds(new Set());
      }
    },
    [rows],
  );

  // ── Validation errors ────────────────────────────────────────────────────

  const [errors, setErrors] = useState<GridValidationErrors>({});

  const setCellError = useCallback(
    (rowId: string, colKey: string, msg: string | null) => {
      setErrors((prev) => {
        const next = { ...prev };
        if (msg) {
          next[rowId] = { ...(next[rowId] ?? {}), [colKey]: msg };
        } else {
          const rowErrors = { ...(next[rowId] ?? {}) };
          delete rowErrors[colKey];
          if (Object.keys(rowErrors).length === 0) {
            delete next[rowId];
          } else {
            next[rowId] = rowErrors;
          }
        }
        return next;
      });
    },
    [],
  );

  // ── Inline cell change ───────────────────────────────────────────────────

  const handleCellChange = useCallback(
    (rowId: string, col: GridColumn, val: unknown) => {
      const next = rowsRef.current.map((r) =>
        r._rowId === rowId ? (setCellValue(r, col, val) as GridRow) : r,
      );
      setRows(next);
      emitChange(next);
      const err = validateCell(val, col);
      setCellError(rowId, col.key, err);
    },
    [emitChange, setCellError],
  );

  // ── Pagination ───────────────────────────────────────────────────────────

  const [page, setPage] = useState(1);
  const paginationEnabled = features.pagination && pageSize && pageSize > 0;
  const displayRows = paginationEnabled
    ? getPage(rows, page, pageSize!)
    : rows;

  const pages = paginationEnabled ? totalPages(rows.length, pageSize!) : 1;

  // ── Drag reorder ─────────────────────────────────────────────────────────

  const dragFromIndex = useRef<number | null>(null);

  // Convert page-local row index to global index in the full rows array
  const toGlobalIndex = useCallback(
    (localIndex: number): number =>
      paginationEnabled ? (page - 1) * pageSize! + localIndex : localIndex,
    [paginationEnabled, page, pageSize],
  );

  const handleDragStart = useCallback(
    (localIndex: number) => {
      dragFromIndex.current = toGlobalIndex(localIndex);
    },
    [toGlobalIndex],
  );

  const handleDrop = useCallback(
    (localIndex: number) => {
      if (dragFromIndex.current === null) return;
      const globalTo = toGlobalIndex(localIndex);
      const next = reorderRows(rowsRef.current, dragFromIndex.current, globalTo);
      dragFromIndex.current = null;
      setRows(next);
      emitChange(next);
    },
    [emitChange, toGlobalIndex],
  );

  useEffect(() => {
    if (page > pages) setPage(Math.max(1, pages));
  }, [page, pages]);

  // ── Modal edit ───────────────────────────────────────────────────────────

  const [editingCell, setEditingCell] = useState<{
    rowId: string;
    formId: number | null;
    mode: 'view' | 'edit';
    saveEndpoint?: string;
    saveMethod?: 'PUT' | 'POST';
    dataRootPath?: string;
  } | null>(null);

  const editingRow = useMemo(
    () => rows.find((r) => r._rowId === editingCell?.rowId) ?? null,
    [rows, editingCell],
  );

  const handleModalSave = useCallback(
    (updated: GridRow) => {
      const next = rowsRef.current.map((r) => (r._rowId === updated._rowId ? updated : r));
      setRows(next);
      emitChange(next);
      setEditingCell(null);
    },
    [emitChange],
  );

  const handleOpenForm = useCallback(
    (
      rowId: string,
      formId: number,
      mode: 'view' | 'edit' = 'edit',
      saveEndpoint?: string,
      saveMethod?: 'PUT' | 'POST',
      dataRootPath?: string,
    ) => {
      setEditingCell({ rowId, formId, mode, saveEndpoint, saveMethod, dataRootPath });
    },
    [],
  );

  // Client-side reflection of a successful `kind: 'delete'` link action — the DELETE
  // request itself already round-tripped to the real backend (see LinkCell.handleClick);
  // this just keeps the grid's own row list in sync afterwards, same as every other
  // row-mutating action in this file (add/deleteSelected/etc.) already does.
  const handleRowDeleted = useCallback(
    (rowId: string) => {
      const next = rowsRef.current.filter((r) => r._rowId !== rowId);
      setRows(next);
      emitChange(next);
      setSelectedIds((prev) => {
        if (!prev.has(rowId)) return prev;
        const next2 = new Set(prev);
        next2.delete(rowId);
        return next2;
      });
    },
    [emitChange],
  );

  // ── Button actions ───────────────────────────────────────────────────────

  const handleButtonClick = useCallback(
    (btn: GridButton) => {
      switch (btn.buttonAction) {
        case 'add': {
          const blankTemplate = Object.fromEntries(
            Object.entries(newEntityTemplate).filter(([, v]) => {
              if (typeof v === 'string') return v === '';
              if (v === false) return false;
              return true;
            })
          );
          const newRow = createNewRow(blankTemplate, columns);
          const next = [...rowsRef.current, newRow];
          setRows(next);
          emitChange(next);
          break;
        }

        case 'deleteSelected': {
          const next = rowsRef.current.filter((r) => !selectedIds.has(r._rowId));
          setRows(next);
          emitChange(next);
          setSelectedIds(new Set());
          break;
        }

        case 'editSelected': {
          const [id] = [...selectedIds];
          if (id) setEditingCell({ rowId: id, formId: editFormID ?? null, mode: 'edit' });
          break;
        }

        case 'addQuickItems': {
          // Inline items take priority; fall back to a named preset.
          const source =
            btn.items ??
            (btn.itemsPreset ? GridItemPresetsRegistry.get(btn.itemsPreset) : undefined) ??
            [];
          // Keep pre-selected values for select/dropdown columns only;
          // strip strings from text columns so they show placeholder instead.
          const selectKeys = new Set(
            columns.filter((c) => c.type === 'select').map((c) => c.key)
          );
          const items = source.map((item) => {
            const blank = Object.fromEntries(
              Object.entries(item).filter(([k, v]) => {
                if (typeof v === 'string' && v !== '' && !selectKeys.has(k)) return false;
                if (v === false) return false;
                return true;
              })
            );
            return createNewRow(blank, columns);
          });
          const next = [...rowsRef.current, ...items];
          setRows(next);
          emitChange(next);
          break;
        }

        default: {
          // Delegate unknown actions to the plugin registry
          const handler = GridActionRegistry.get(btn.buttonAction);
          if (handler) {
            const result = handler({
              rows: rowsRef.current,
              selectedIds,
              config,
              button: btn,
            });
            if (Array.isArray(result)) {
              setRows(result);
              emitChange(result);
            }
          }
          break;
        }
      }
    },
    [columns, config, emitChange, newEntityTemplate, selectedIds],
  );

  const isButtonDisabled = useCallback(
    (btn: GridButton): boolean => {
      if (btn.requiresSelection && selectedIds.size === 0) return true;
      if (btn.selectionLimit !== undefined && selectedIds.size !== btn.selectionLimit) return true;
      return false;
    },
    [selectedIds],
  );

  // ── Export / Import ──────────────────────────────────────────────────────

  const handleExport = useCallback(
    (fmt: 'json' | 'csv') => {
      const content =
        fmt === 'json'
          ? exportToJson(rows, columns, idField)
          : exportToCsv(rows, columns, idField);
      const blob = new Blob([content], { type: fmt === 'json' ? 'application/json' : 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `grid-export.${fmt}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_REVOKE_DELAY_MS);
    },
    [rows, columns, idField],
  );

  const importInputRef = useRef<HTMLInputElement>(null);
  const [pendingImportFmt, setPendingImportFmt] = useState<'json' | 'csv' | null>(null);

  const handleImportClick = useCallback((fmt: 'json' | 'csv') => {
    setPendingImportFmt(fmt);
    importInputRef.current?.click();
  }, []);

  const handleImportFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !pendingImportFmt) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const text = String(ev.target?.result ?? '');
        const parsed =
          pendingImportFmt === 'json' ? importFromJson(text) : importFromCsv(text);
        const stamped = parsed.map(stampRowId);
        const next = [...rowsRef.current, ...stamped];
        setRows(next);
        emitChange(next);
      };
      reader.readAsText(file);
      // Reset so the same file can be re-imported
      e.target.value = '';
      setPendingImportFmt(null);
    },
    [emitChange, pendingImportFmt],
  );

  // ── Column helpers ───────────────────────────────────────────────────────

  const dataColumns = columns.filter((c) => c.type !== 'selector');
  const hasSelector = columns.some((c) => c.type === 'selector');

  // ── Frozen/pinned columns ────────────────────────────────────────────────
  // Offsets for `col.freezePosition` (see grid.types.ts's GridColumn doc comment). Only a
  // contiguous LEADING run of `'left'`-frozen columns (and, symmetrically, a contiguous
  // TRAILING run of `'right'`-frozen columns) is supported — the standard, and by far most
  // common, data-grid freeze pattern (an anchor column pinned at the very edge, not an
  // arbitrary column floating mid-table). Falls back to a sensible 160px per column when
  // `col.width` isn't set, since `position: sticky`'s offset must be a real pixel value known
  // ahead of layout — this table's normal `table-layout: auto` sizing can't be used for it.
  const freezeLeftOffsets = useMemo(() => {
    const offsets = new Map<string, number>();
    let acc = 0;
    for (const col of dataColumns) {
      if (col.freezePosition !== 'left') break;
      offsets.set(col.key, acc);
      acc += col.width ?? 160;
    }
    return offsets;
  }, [dataColumns]);
  const freezeRightOffsets = useMemo(() => {
    const offsets = new Map<string, number>();
    let acc = 0;
    for (let i = dataColumns.length - 1; i >= 0; i--) {
      const col = dataColumns[i];
      if (col.freezePosition !== 'right') break;
      offsets.set(col.key, acc);
      acc += col.width ?? 160;
    }
    return offsets;
  }, [dataColumns]);
  const getFreezeStyle = useCallback(
    (col: GridColumn, isHeader: boolean): React.CSSProperties | undefined => {
      // Solid, fully-opaque colors only -- `position: sticky` needs to visually OCCLUDE
      // whatever scrolls underneath it. `.af-grid__thead-row`'s own real background is the
      // translucent `rgba(255,255,255,0.03)` overlay (a subtle tint meant to sit on top of
      // `.af-grid`'s own solid `#1a1f2e`, in normal non-overlapping flow) -- reusing that same
      // rgba() here as a sticky cell's background left it ~97% transparent, so a scrolled-past
      // header's text visibly bled through the "frozen" cell (confirmed via a zoomed
      // screenshot: "NAME" and the tail of "FILE NAME" overlapping at the same pixels).
      // `#1d2333` is this composite's solid equivalent (`.af-grid`'s base dark tone with that
      // same faint highlight baked in, close enough to be visually seamless).
      const rowBg = theme === 'dark' ? '#1a1f2e' : '#ffffff';
      const headerBg = theme === 'dark' ? '#1d2333' : '#f9fafb';
      if (freezeLeftOffsets.has(col.key)) {
        return {
          position: 'sticky', left: freezeLeftOffsets.get(col.key), zIndex: isHeader ? 3 : 2,
          background: isHeader ? headerBg : rowBg,
          boxShadow: '2px 0 4px rgba(0,0,0,0.15)',
        };
      }
      if (freezeRightOffsets.has(col.key)) {
        return {
          position: 'sticky', right: freezeRightOffsets.get(col.key), zIndex: isHeader ? 3 : 2,
          background: isHeader ? headerBg : rowBg,
          boxShadow: '-2px 0 4px rgba(0,0,0,0.15)',
        };
      }
      return undefined;
    },
    [freezeLeftOffsets, freezeRightOffsets, theme],
  );

  // Auto-show checkboxes when any button requires selection, even without an
  // explicit selector column — avoids schema boilerplate for common cases.
  const needsSelection = buttons.some((b) => b.requiresSelection);
  const showCheckbox = hasSelector || needsSelection;

  const allSelected = useMemo(
    () => rows.length > 0 && rows.every((r) => selectedIds.has(r._rowId)),
    [rows, selectedIds],
  );
  const someSelected = useMemo(
    () => rows.some((r) => selectedIds.has(r._rowId)),
    [rows, selectedIds],
  );

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className={`af-grid ${theme}${tableClass ? ` ${tableClass}` : ''}`}>
      {/* Toolbar — hidden in view mode */}
      {mode !== 'view' && <div className="af-grid__toolbar">
        {/* Action buttons */}
        {buttons.map((btn) => (
          <button
            key={`${btn.buttonAction}__${btn.label}`}
            className="af-grid__btn"
            disabled={isButtonDisabled(btn)}
            onClick={() => handleButtonClick(btn)}
            type="button"
          >
            {btn.emoji
              ? <span className="af-grid__btn-emoji" aria-hidden="true">{btn.emoji} </span>
              : btn.icon
                ? <span className={`af-grid__btn-icon icon-${btn.icon}`} aria-hidden="true" />
                : null}
            {btn.label}
          </button>
        ))}

        {/* Export / import in a separate group */}
        {(features.exportFormats?.length || features.importFormats?.length) ? (
          <span className="af-grid__toolbar-sep" />
        ) : null}

        {features.exportFormats?.includes('json') && (
          <button className="af-grid__btn af-grid__btn--ghost" onClick={() => handleExport('json')} type="button">
            Export JSON
          </button>
        )}
        {features.exportFormats?.includes('csv') && (
          <button className="af-grid__btn af-grid__btn--ghost" onClick={() => handleExport('csv')} type="button">
            Export CSV
          </button>
        )}
        {features.importFormats?.includes('json') && (
          <button className="af-grid__btn af-grid__btn--ghost" onClick={() => handleImportClick('json')} type="button">
            Import JSON
          </button>
        )}
        {features.importFormats?.includes('csv') && (
          <button className="af-grid__btn af-grid__btn--ghost" onClick={() => handleImportClick('csv')} type="button">
            Import CSV
          </button>
        )}
        <input
          ref={importInputRef}
          type="file"
          accept={pendingImportFmt === 'csv' ? '.csv,text/csv' : '.json,application/json'}
          style={{ display: 'none' }}
          onChange={handleImportFile}
        />
      </div>}

      {/* Table (viewMode 'grid', the default) — completely unchanged by Task 19. */}
      {viewMode === 'grid' && (
      <div className="af-grid__table-wrapper">
        <table className="af-grid__table" aria-label={config.title}>
          <thead>
            <tr className="af-grid__thead-row">
              {showCheckbox && isMultiSelect && (
                <th className="af-grid__th af-grid__th--selector">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected && !allSelected;
                    }}
                    onChange={(e) => selectAll(e.target.checked)}
                    aria-label="Select all"
                  />
                </th>
              )}
              {showCheckbox && !isMultiSelect && (
                <th className="af-grid__th af-grid__th--selector" />
              )}
              {dataColumns.map((col) => (
                <th
                  key={col.key}
                  className="af-grid__th"
                  style={{ ...(col.width ? { width: col.width } : undefined), ...getFreezeStyle(col, true) }}
                >
                  {col.label}
                  {col.required && col.type !== 'link' && <span className="af-grid__required" aria-label="required"> *</span>}
                </th>
              ))}
              {features.reorder && <th className="af-grid__th af-grid__th--reorder" />}
            </tr>
          </thead>
          <tbody>
            {displayRows.length === 0 && (
              <tr>
                <td
                  colSpan={dataColumns.length + (showCheckbox ? 1 : 0) + (features.reorder ? 1 : 0)}
                  className="af-grid__empty"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
            {displayRows.map((row, rowIndex) => {
              const isSelected = selectedIds.has(row._rowId);
              const isAlt = altRowClass && rowIndex % 2 === 1;
              return (
                <tr
                  key={row._rowId}
                  className={[
                    'af-grid__row',
                    isSelected ? 'af-grid__row--selected' : '',
                    isAlt ? altRowClass : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  draggable={!!features.reorder}
                  onDragStart={() => handleDragStart(rowIndex)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(rowIndex)}
                >
                  {/* Selector cell — always interactive regardless of bulkEditEnabled */}
                  {showCheckbox && (
                    <td className="af-grid__td af-grid__td--selector">
                      <input
                        type={isMultiSelect ? 'checkbox' : 'radio'}
                        checked={isSelected}
                        onChange={() => toggleSelect(row._rowId)}
                        aria-label={`Select row ${rowIndex + 1}`}
                      />
                    </td>
                  )}

                  {/* Data cells */}
                  {dataColumns.map((col) => {
                    // ── Link column ────────────────────────────────────────────
                    if (col.type === 'link') {
                      return (
                        <LinkCell
                          key={col.key}
                          col={col}
                          row={row}
                          onOpenForm={handleOpenForm}
                          onRowDeleted={handleRowDeleted}
                          freezeStyle={getFreezeStyle(col, false)}
                        />
                      );
                    }

                    const cellValue = col.type === 'computed'
                      ? evaluateCompute(col.compute ?? 'undefined', row)
                      : getCellValue(row, col);

                    const dynamicClass = col.cellClassExpr
                      ? evaluateCellClass(col.cellClassExpr, cellValue, row)
                      : '';

                    const tdClass = [
                      'af-grid__td',
                      col.columnCss ?? '',
                      dynamicClass,
                    ]
                      .filter(Boolean)
                      .join(' ');

                    const cellError = errors[row._rowId]?.[col.key];

                    const isEditable =
                      bulkEditEnabled &&
                      col.type !== 'computed' &&
                      col.type !== 'selector';

                    return (
                      <td key={col.key} className={tdClass} style={getFreezeStyle(col, false)}>
                        {isEditable ? (
                          <>
                            <CellEditor
                              col={col}
                              value={cellValue}
                              error={cellError}
                              onChange={(val) => handleCellChange(row._rowId, col, val)}
                              onBlur={() => {
                                const currentRow = rowsRef.current.find((r) => r._rowId === row._rowId);
                                const currentVal = currentRow ? getCellValue(currentRow, col) : cellValue;
                                const err = validateCell(currentVal, col);
                                setCellError(row._rowId, col.key, err);
                              }}
                            />
                            {cellError && (
                              <span className="af-grid__cell-error" role="alert">
                                {cellError}
                              </span>
                            )}
                          </>
                        ) : (
                          renderReadOnlyCell(col, cellValue)
                        )}
                      </td>
                    );
                  })}

                  {/* Drag handle */}
                  {features.reorder && (
                    <td className="af-grid__td af-grid__td--reorder" aria-label="Drag to reorder">
                      <span className="af-grid__drag-handle" aria-hidden="true">⠿</span>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {/* Cards / gallery (viewMode 'cards' | 'gallery', Task 19) — same displayRows/selection/
          row-action plumbing as the table above, different per-row markup. Frozen-column
          concept doesn't apply here (skipped, per that feature's own doc comment). */}
      {viewMode !== 'grid' && (
        <GridCardView
          rows={displayRows}
          columns={dataColumns}
          cardTemplate={cardTemplate}
          variant={viewMode}
          emptyMessage={emptyMessage}
          showCheckbox={showCheckbox}
          isMultiSelect={isMultiSelect}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onOpenForm={handleOpenForm}
          onRowDeleted={handleRowDeleted}
        />
      )}

      {/* Pagination */}
      {paginationEnabled && pages > 1 && (
        <div className="af-grid__pagination">
          <button
            className="af-grid__page-btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            type="button"
            aria-label="Previous page"
          >
            ‹
          </button>
          <span className="af-grid__page-info">
            {page} / {pages}
          </span>
          <button
            className="af-grid__page-btn"
            disabled={page >= pages}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            type="button"
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      )}

      {/* Edit modal */}
      {editingRow && editingCell && (
        <GridEditModal
          key={editingCell.rowId}
          row={editingRow}
          columns={columns}
          editFormID={editingCell.formId}
          formMode={editingCell.mode}
          saveEndpoint={editingCell.saveEndpoint}
          saveMethod={editingCell.saveMethod}
          dataRootPath={editingCell.dataRootPath}
          onSave={handleModalSave}
          onCancel={() => setEditingCell(null)}
        />
      )}
    </div>
  );
};

GridControl.displayName = 'GridControl';
